import React, { useState, useCallback } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'

import Challenges from './Challenges';
import HeroSection from './HeroSection'
import ScoreCard from './ScoreCard';
import PreparationList from './PreparationList'
import RecentAttempts from './RecentAttempts'
import { AssessmentEntryPoint, CaseMasterScreens } from './constant';
import CreatePersonalizedChallenge from './CreatePersonalizedChallenge';
import FeedbackPendingCard from './FeedbackPendingCard';
import { getCaseMasterProblems, getRecentCaseMasterAttempts } from 'services';
import {
  Session, getCaseMasterPlan, isB2cCaseMasterUser, hasFullCaseMasterAccess,
  CaseMasterPlans,
} from 'utils';
import { CaseMasterAccessProvider } from './CaseMasterAccessContext';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
  }
}));

const CaseMaster = () => {
  const classes = useStyles();

  const [activeScreen, setActiveScreen] = useState(CaseMasterScreens.HOME);

  const [hasPendingAttempt, setHasPendingAttempt] = useState(false);
  const [pendingAttemptCardShown, setPendingAttemptCardShown] = useState(false);

  const [entryPoint, setEntryPoint] = useState({ data: 'case_master' });

  // DEBUG(casemaster-plan): temporary logging to inspect the B2C plan inputs and
  // (for free users) the flagged free case + attempt-spent state. Remove before merge.
  React.useEffect(() => {
    (async () => {
      const metadata = Session.getUserMetadata();
      console.log('[CaseMaster][DEBUG] metadata:', metadata);
      console.log('[CaseMaster][DEBUG] isB2cCaseMasterUser:', isB2cCaseMasterUser());
      console.log('[CaseMaster][DEBUG] casemasterPlanId:', metadata?.casemasterPlanId, '→ resolved:', getCaseMasterPlan());
      console.log('[CaseMaster][DEBUG] validTill:', metadata?.validTill);
      console.log('[CaseMaster][DEBUG] hasFullCaseMasterAccess:', hasFullCaseMasterAccess());

      if (!isB2cCaseMasterUser() || getCaseMasterPlan() !== CaseMasterPlans.FREE) return;

      try {
        const [problemsRes, { attempts }] = await Promise.all([
          getCaseMasterProblems({ isFreeCase: true, isActive: true }),
          getRecentCaseMasterAttempts({ user: Session.userId }),
        ]);

        const freeCase = problemsRes?.casemasterProblems?.[0] || null;
        const freeAttemptUsed = Array.isArray(attempts) &&
          attempts.some((a) => a?.status === 'feedback_generated');

        console.log('[CaseMaster][DEBUG] flagged free case:',
          freeCase ? { _id: freeCase._id, title: freeCase.title, topic: freeCase.topic } : '⚠️ none returned');
        console.log('[CaseMaster][DEBUG] freeAttemptUsed:', freeAttemptUsed);
      } catch (err) {
        console.error('[CaseMaster][DEBUG] ❌ free-plan lookup failed:', err?.message);
      }
    })();
  }, []);

  // These screens swap by state, not by route, so the browser keeps the scroll
  // position. Opening the case list from a preparation card - which sits well
  // down the home screen - would otherwise land part-way down the new screen.
  // The scroll lives on AppShell's content box, not the window, and this runs
  // before paint so the list is never shown at the old offset.
  React.useLayoutEffect(() => {
    const container = document.querySelector('.appShellContent');

    if (container) container.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, [activeScreen]);

  const handleAttemptStatusCheck = useCallback(({ hasPending }) => {
    if (hasPending && !pendingAttemptCardShown) {
      setHasPendingAttempt(true);
    }
  }, [pendingAttemptCardShown]);

  const handleClosePendingCard = useCallback(() => {
    setHasPendingAttempt(false);
    setPendingAttemptCardShown(true);
  }, []);

  // Preparation cards no longer scope the list to their topic, so they open the
  // same unfiltered case list as the hero card. Nothing is left to carry a
  // tagId, hence the bare general-case entry point.
  const handleCardClick = useCallback(() => {
    setEntryPoint({ data: AssessmentEntryPoint.GENERAL_CASE_STUDY });
    setActiveScreen(CaseMasterScreens.CHALLENGES);
  }, []);

  const handleHeroScreenChange = useCallback((screen) => {
    if (screen === CaseMasterScreens.CHALLENGES) {
      setEntryPoint({ data: AssessmentEntryPoint.GENERAL_CASE_STUDY });
    }
    setActiveScreen(screen);
  }, []);

  const handleChallengesBack = useCallback(() => {
    setActiveScreen(CaseMasterScreens.HOME);
  }, []);

  const handlePersonalizedBack = useCallback(() => {
    setActiveScreen(CaseMasterScreens.HOME);
  }, []);

  return (
    <CaseMasterAccessProvider>
    <Box className={classes.root}>
    {
      activeScreen === CaseMasterScreens.HOME &&
        <>
          {/* {
            hasPendingAttempt && <FeedbackPendingCard setPendingAttempt={handleClosePendingCard}/>
          } */}
          {/* <ScoreCard /> */}
          <HeroSection setActiveScreen={handleHeroScreenChange} />
          <PreparationList onClick={handleCardClick} />
          <RecentAttempts onAttemptStatusCheck={handleAttemptStatusCheck}/>
        </>
    }
    {
      activeScreen === CaseMasterScreens.CHALLENGES &&
        <Challenges
          entryPoint={entryPoint}
          handleBack={handleChallengesBack}
        />
    }
    {
      activeScreen === CaseMasterScreens.PERSONALIZED &&
        <CreatePersonalizedChallenge handleBack={handlePersonalizedBack} />
    }
    </Box>
    </CaseMasterAccessProvider>
  )
}

export default CaseMaster;
